import { useState, useRef } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import "./CertificateGenerator.css";

import engineering from "../assets/eram-engineering.png";
import talent from "../assets/eram-talent.png";
import arabia from "../assets/eram-arabia.png";
import holdings from "../assets/eram-holdings.png";
import motors from "../assets/eram-motors.png";
import properties from "../assets/eram-properties.png";
import education from "../assets/eram-education.png";

function CertificateGenerator() {
  const generateId = () =>
    "CRT-" + Math.floor(100000 + Math.random() * 900000);

  const emptyForm = {
    name: "",
    course: "",
    date: "",
    instructor: "",
    director: "",
    certificateType: "completion",
    customType: "",
    template: "engineering",
  };

  const [form, setForm] = useState(emptyForm);
  const [certificateId] = useState(generateId());
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const certRef = useRef(null);

  // ✅ ALL TEMPLATES ADDED
  const templates = {
    engineering,
    talent,
    arabia,
    holdings,
    motors,
    properties,
    education,
  };

  const content = {
    completion: ["CERTIFICATE", "OF COMPLETION", "For successfully completing the course"],
    participation: ["CERTIFICATE", "OF PARTICIPATION", "For active participation in"],
    achievement: ["CERTIFICATE", "OF ACHIEVEMENT", "For outstanding achievement in"],
    excellence: ["CERTIFICATE", "OF EXCELLENCE", "In recognition of excellence in"],
    training: ["TRAINING", "CERTIFICATE", "For successfully completing training in"],
  };

  const [title, subtitle, text] =
    form.certificateType === "custom"
      ? [form.customType || "CERTIFICATE", "", "This certificate is proudly presented to"]
      : content[form.certificateType] || content["completion"];

  // USERS FETCH
  const fetchAllUsers = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth?page=1&limit=50&q=`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setUserSuggestions(data.users || []);
      setShowSuggestions(true);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchUsers = async (query) => {
    if (!query) return fetchAllUsers();

    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth?page=1&limit=50&q=${query}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setUserSuggestions(data.users || []);
      setShowSuggestions(true);
    } catch (err) {
      console.log(err);
    }
  };

  const change = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]:
        field === "date" ||
        field === "certificateType" ||
        field === "template"
          ? value
          : value.toUpperCase(),
    }));
  };

  const download = async () => {
    const canvas = await html2canvas(certRef.current, { scale: 2 });

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [canvas.width, canvas.height],
    });

    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0);
    pdf.save(`${form.name || "certificate"}.pdf`);
  };

  return (
    <div className="certificate-generator-wrapper container">

      <h1 className="page-title">🎓 Certificate Generator</h1>

      <div className="form">

        {/* ✅ FULL TEMPLATE DROPDOWN */}
        <select
          value={form.template}
          onChange={(e) => change("template", e.target.value)}
        >
          <option value="engineering">ERAM Engineering</option>
          <option value="talent">ERAM Talent</option>
          <option value="arabia">ERAM Arabia</option>
          <option value="holdings">ERAM Holdings</option>
          <option value="motors">ERAM Motors</option>
          <option value="properties">ERAM Properties</option>
          <option value="education">ERAM Education</option>
        </select>

        {/* CERTIFICATE TYPE */}
        <select
          value={form.certificateType}
          onChange={(e) => change("certificateType", e.target.value)}
        >
          {Object.keys(content).map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
          <option value="custom">Custom (type below)</option>
        </select>

        {/* CUSTOM */}
        {form.certificateType === "custom" && (
          <input
            type="text"
            value={form.customType}
            placeholder="e.g. OF HONOR / OF DISTINCTION"
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                customType: e.target.value.toUpperCase(),
              }))
            }
          />
        )}

        {/* NAME INPUT */}
        <div style={{ position: "relative", width: "320px", margin: "10px auto" }}>
          <input
            type="text"
            value={form.name}
            placeholder="Name"
            onChange={(e) => {
              change("name", e.target.value);
              fetchUsers(e.target.value);
            }}
            onFocus={fetchAllUsers}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          />

          {showSuggestions && userSuggestions.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #ccc",
              borderRadius: "6px",
              zIndex: 999,
              maxHeight: "200px",
              overflowY: "auto",
            }}>
              {userSuggestions.map((u) => (
                <div
                  key={u._id}
                  onMouseDown={() => {
                    change("name", u.fullName || u.name || u.email);
                    setShowSuggestions(false);
                    setUserSuggestions([]);
                  }}
                  style={{
                    padding: "10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>
                    {u.fullName || u.name}
                  </div>
                  <div style={{ fontSize: "11px", color: "#777" }}>
                    {u.email}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* INPUTS */}
        {["course", "date", "instructor", "director"].map((f) => (
          <input
            key={f}
            type={f === "date" ? "date" : "text"}
            value={form[f]}
            placeholder={f.toUpperCase()}
            onChange={(e) => change(f, e.target.value)}
          />
        ))}

        <button onClick={download}>Download PDF</button>
      </div>

      {/* CERTIFICATE PREVIEW */}
      <div ref={certRef} className="certificate">

        <div className="certificate-border">

          <div className="certificate-logo">
            <img
              src={templates[form.template]}
              alt="ERAM Certificate"
              className="logo-img"
            />
          </div>

          <h1 className="title">CERTIFICATE</h1>

          <h2 className="subtitle">
            {form.certificateType === "custom"
              ? form.customType || "OF ..."
              : title === "CERTIFICATE"
                ? subtitle
                : `${title} ${subtitle}`}
          </h2>

          <p className="text">This certificate is proudly presented to</p>

          <div className="name">{form.name || "STUDENT NAME"}</div>

          <p className="text">{text}</p>

          <div className="course">{form.course || "COURSE NAME"}</div>

          <div className="info">

            <div className="info-box">
              <div className="label">DATE</div>
              <div className="value">
                {form.date
                  ? new Date(form.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </div>
            </div>

            <div className="info-box">
              <div className="label">CERTIFICATE ID</div>
              <div className="value" style={{ fontSize: "16px" }}>
                {certificateId}
              </div>
            </div>

            <div className="info-box">
              <div className="sub-label">INSTRUCTOR</div>
              <div className="sub-value">{form.instructor || "—"}</div>

              <div className="sub-label" style={{ marginTop: "20px" }}>
                DIRECTOR
              </div>
              <div className="sub-value">{form.director || "—"}</div>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
}

export default CertificateGenerator;